import React, { useEffect, useRef } from 'react';
import { Animated, Easing, Modal, Platform, Pressable, StyleSheet, View } from 'react-native';

interface DesktopSidePanelProps {
	visible: boolean;
	onClose: () => void;
	width?: number; // pixels
	children: React.ReactNode;
	showOverlay?: boolean;
	closeOnOverlayPress?: boolean;
}

export const DesktopSidePanel: React.FC<DesktopSidePanelProps> = ({
	visible,
	onClose,
	width = 480,
	children,
	showOverlay = true,
	closeOnOverlayPress = true,
}) => {
	const translateX = useRef(new Animated.Value(width)).current;

	useEffect(() => {
		if (visible) {
			Animated.timing(translateX, {
				toValue: 0,
				duration: 200,
				easing: Easing.out(Easing.cubic),
				useNativeDriver: true,
			}).start();
		} else {
			Animated.timing(translateX, {
				toValue: width,
				duration: 200,
				easing: Easing.in(Easing.cubic),
				useNativeDriver: true,
			}).start();
		}
	}, [visible, translateX, width]);

	// ESC key to close on web
	useEffect(() => {
		if (Platform.OS !== 'web') return;
		const w: any = (globalThis as any)?.window;
		if (!w) return;
		const handler = (e: any) => {
			if (e.key === 'Escape' && visible) onClose();
		};
		w.addEventListener('keydown', handler);
		return () => w.removeEventListener('keydown', handler);
	}, [visible, onClose]);

	return (
		<Modal
			visible={visible}
			transparent
			animationType="none"
			onRequestClose={onClose}
		>
			<View style={styles.container}>
				{showOverlay && (
					<Pressable
						style={styles.overlay}
						onPress={closeOnOverlayPress ? onClose : undefined}
						pointerEvents={visible ? 'auto' : 'none'}
					/>
				)}
				<Animated.View
					style={[
						styles.panel,
						{ width },
						{ transform: [{ translateX }] },
					]}
				>
					{children}
				</Animated.View>
			</View>
		</Modal>
	);
};

const styles = StyleSheet.create({
	container: {
		flex: 1,
		flexDirection: 'row',
	},
	overlay: {
		flex: 1,
		backgroundColor: 'rgba(0,0,0,0.35)',
	},
	panel: {
		height: '100%',
		backgroundColor: 'white',
		shadowColor: '#000',
		shadowOpacity: 0.2,
		shadowRadius: 8,
		shadowOffset: { width: -2, height: 0 },
		elevation: 8,
		overflow: 'visible',
	},
});
